import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IState } from '@core/interfaces/istate';
import { IGlobalAnnouncement } from '@core/interfaces/iglobal-announcement';
import { GlobalAnnouncementService } from '@core/services/global-announcement.service';
import { take } from 'rxjs';
import { CustomValidators } from '@core/validators/custom.validator';
import { toLocalDayEndUtc, toLocalDayStartUtc } from '@core/services/helper.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-global-announcement-modal',
  templateUrl: './add-global-annoucements-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddGlobalAnnoucementsModalComponent implements OnInit {
  isEditMode = false;
  announcement: IGlobalAnnouncement = {} as IGlobalAnnouncement;
  isProcessing = false;

  form!: FormGroup;
  readonly MESSAGE_MAX = 1000;
  searchStateId: number | null = null;
  clearDropdowns: boolean = false;
  allStates: boolean = false;

  iconOptions: { label: string; value: string }[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public editData: any,
    public dialogRef: MatDialogRef<AddGlobalAnnoucementsModalComponent>,
    private fb: FormBuilder,
    private announcementService: GlobalAnnouncementService,
    private translocoService: TranslocoService,
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.iconOptions = [
      { label: this.translocoService.translate('configuration.common.no'), value: 'none' },
      { label: this.translocoService.translate('configuration.common.yes'), value: 'exclamation-triangle' },
    ];
    this.isEditMode = !!this.editData?.data;
    this.announcement = this.editData?.data || {} as IGlobalAnnouncement;

    this.initializeForm();
    this.allStates = this.announcement.stateId == null;
    if (this.isEditMode) {
      this.form.patchValue({
        ...this.announcement,
        icon: this.announcement.icon ?? 'none',
      });
    }
    this.updateStateIdValidators();
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      stateId: [null],
      message: ['', [Validators.required, Validators.maxLength(this.MESSAGE_MAX)]],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      isActive: [true],
      icon: ['none'],
    },{
      validators: [
      CustomValidators.dateValidator('startDate', 'endDate'),
      CustomValidators.sameDateValidator('startDate', 'endDate'),
      ],
    });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const request: IGlobalAnnouncement = {
      ...this.form.value,
      id: this.isEditMode ? this.announcement.id : 0,
      title: (this.form.value.message || '').substring(0, 100),
      icon: this.form.value.icon === 'none' ? null : this.form.value.icon,
      startDate: toLocalDayStartUtc(this.form.value.startDate),
      endDate: toLocalDayEndUtc(this.form.value.endDate),
    };

    this.isProcessing = true;
    const action = this.isEditMode
      ? this.announcementService.update(request)
      : this.announcementService.add(request);

    action.pipe(take(1)).subscribe({
      next: (success) => {
        this.isProcessing = false;
        if(success) {
       this.dialogRef.close({ success: true });
        }
      },
      error: () => {
        this.isProcessing = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  isInvalid(name: string): boolean {
    const c = this.form.get(name);
    return !!(c?.invalid && c?.touched);
  }

    onStateChange(state: IState): void {
    this.searchStateId = state?.id ?? null;
    this.form.patchValue({ stateId: state?.id ?? null });
  }

  onAllStatesChange(): void {
    this.allStates = !this.allStates;
    if(this.allStates) {
      this.searchStateId = null;
      this.form.patchValue({stateId: null});
      this.clearDropdowns = true;
    }else {
      this.clearDropdowns = false;
    }
    this.updateStateIdValidators();
  }

  private updateStateIdValidators(): void {
    const stateIdControl = this.form.get('stateId');
    if (!stateIdControl) return;
    if (this.allStates) {
      stateIdControl.clearValidators();
    } else {
      stateIdControl.setValidators([Validators.required]);
    }
    stateIdControl.updateValueAndValidity();
  }
}
